import { useState } from 'react';
import { Card, Input, Table, Tag, Button } from 'antd';
import { IconEye } from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';
import { useSizEmployees } from '@/entities/siz';
import { PageSpinner } from '@/shared/ui/PageSpinner';
import type { PpeEmployeeList } from '@/entities/siz';

interface EmployeesTabProps {
  focusEmployeeId?: string;
}

const STATUS_LABELS: Record<string, { label: string; color: string }> = {
  pending: { label: 'Ожидает', color: 'default' },
  partially_issued: { label: 'Частично', color: 'warning' },
  issued: { label: 'Полностью', color: 'success' },
  expiring_soon: { label: 'Истекает', color: 'warning' },
  overdue: { label: 'Просрочено', color: 'error' },
};

export function EmployeesTab(_props: EmployeesTabProps) {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const { data, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } = useSizEmployees({
    q: search || undefined,
  });

  const employees = data?.pages.flatMap((page) => page.employees) || [];

  const columns = [
    {
      title: 'ФИО',
      dataIndex: 'fullName',
      key: 'fullName',
      render: (text: string, record: PpeEmployeeList) => (
        <div>
          <div>{text}</div>
          {record.tabNumber && <div style={{ fontSize: 12, color: '#888' }}>Таб. № {record.tabNumber}</div>}
        </div>
      ),
    },
    {
      title: 'Подразделение',
      dataIndex: ['orgUnit', 'name'],
      key: 'orgUnit',
    },
    {
      title: 'Должность',
      dataIndex: 'position',
      key: 'position',
      render: (position?: { name: string }) => position?.name || '—',
    },
    {
      title: 'Статус комплекта',
      dataIndex: 'kitStatus',
      key: 'kitStatus',
      width: 150,
      render: (status?: string) => {
        if (!status) return '—';
        const config = STATUS_LABELS[status];
        return <Tag color={config?.color}>{config?.label || status}</Tag>;
      },
    },
    {
      title: 'Проблемные позиции',
      dataIndex: 'criticalItemsCount',
      key: 'criticalItemsCount',
      width: 150,
      render: (count: number) => (count > 0 ? <Tag color="error">{count}</Tag> : '—'),
    },
    {
      title: '',
      key: 'actions',
      width: 80,
      render: (_: unknown, record: PpeEmployeeList) => (
        <Button type="text" icon={<IconEye size={18} />} onClick={() => navigate(`/employees/${record.id}?tab=siz`)} />
      ),
    },
  ];

  if (isLoading) return <PageSpinner />;

  return (
    <Card
      title="Сотрудники СИЗ"
      extra={
        <Input.Search
          placeholder="Поиск по ФИО, таб. №..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          allowClear
          style={{ width: 300 }}
        />
      }
    >
      <Table<PpeEmployeeList>
        columns={columns}
        dataSource={employees}
        rowKey="id"
        pagination={false}
        scroll={{ x: 'max-content' }}
        onScroll={(e) => {
          const target = e.target as HTMLDivElement;
          if (
            target.scrollHeight - target.scrollTop - target.clientHeight < 100 &&
            hasNextPage &&
            !isFetchingNextPage
          ) {
            fetchNextPage();
          }
        }}
      />
    </Card>
  );
}
